import React, {useState} from "react";
import {Box} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import SubscriptIcon from "@mui/icons-material/Subscript";
import SuperscriptIcon from "@mui/icons-material/Superscript";
import {useTranslation} from "react-i18next";
import type {Editor} from "@tiptap/core";
import type {} from "@tiptap/extension-invisible-characters";
import {ToolbarIconButton, ToolbarPopover} from "./toolbarControls";
import {toggleSelectedPrintoutVariableMark} from "./printoutToolbarState";
import type {ToolbarState} from "./printoutToolbarState";

interface Props {
    editor: Editor | null;
    state: ToolbarState;
}

const ToolbarMoreMenu = ({editor, state}: Props) => {

    const {t} = useTranslation();
    const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

    const moreMenuOpen = Boolean(moreMenuAnchorEl);

    const runCommand = (command: (editor: Editor) => void) => {
        if (!editor) return;
        command(editor);
    };

    return (
        <>
            <ToolbarIconButton
                active={moreMenuOpen}
                disabled={!editor}
                label={t('More')}
                onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
            >
                <MoreHorizIcon fontSize="small"/>
            </ToolbarIconButton>

            <ToolbarPopover
                anchorEl={moreMenuAnchorEl}
                direction="row"
                onClose={() => setMoreMenuAnchorEl(null)}
                open={moreMenuOpen}
            >
                <ToolbarIconButton
                    active={state.isStrike}
                    disabled={!state.canStrike}
                    label={t('Strike')}
                    onClick={() => runCommand((editor) => {
                        if (toggleSelectedPrintoutVariableMark(editor, 'strike')) return;
                        editor.chain().focus().toggleStrike().run();
                    })}
                >
                    <StrikethroughSIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    active={state.isCode}
                    disabled={!state.canCode}
                    label={t('Code')}
                    onClick={() => runCommand((editor) => editor.chain().focus().toggleCode().run())}
                >
                    <CodeIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    active={state.isSuperscript}
                    disabled={!state.canSuperscript}
                    label={t('Superscript')}
                    onClick={() => runCommand((editor) => editor.chain().focus().toggleSuperscript().run())}
                >
                    <SuperscriptIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    active={state.isSubscript}
                    disabled={!state.canSubscript}
                    label={t('Subscript')}
                    onClick={() => runCommand((editor) => editor.chain().focus().toggleSubscript().run())}
                >
                    <SubscriptIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    active={state.isBlockquote}
                    disabled={!state.canBlockquote && !state.isBlockquote}
                    label={t('Block quote')}
                    onClick={() => runCommand((editor) => editor.chain().focus().toggleBlockquote().run())}
                >
                    <FormatQuoteIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    active={state.invisibleCharactersVisible}
                    disabled={!editor}
                    label={t('Show formatting marks')}
                    onClick={() => runCommand((currentEditor) => (
                        currentEditor.chain().focus().toggleInvisibleCharacters().run()
                    ))}
                >
                    <Box component="span" sx={{fontSize: 15, fontWeight: 700, lineHeight: 1}}>¶</Box>
                </ToolbarIconButton>
            </ToolbarPopover>
        </>
    );
};

export default ToolbarMoreMenu;
